/**
 * ACRES Admin — Promo Banner Manager
 * Configure & schedule the entry-popup that entices users to engage with ACRES.
 */
import React, { useEffect, useState } from 'react';
import { acresAdmin } from '../../../lib/services/acresService';

interface Banner {
  id?: string; version_label: string; title: string; body_md: string;
  cta_label: string; cta_url: string; image_url?: string;
  bg_color?: string; text_color?: string;
  show_to_segment: 'all' | 'kyc_complete' | 'has_shares' | 'no_shares';
  show_frequency: 'once' | 'daily' | 'weekly' | 'every_login';
  start_at?: string | null; end_at?: string | null;
  is_enabled: boolean; is_current: boolean;
}

const empty: Banner = {
  version_label: '', title: '🎁 Earn Real Aureus Shares', body_md: 'Complete simple tasks. Build ownership. Win monthly prizes.',
  cta_label: 'Start Earning', cta_url: '/dashboard#acres',
  bg_color: '#0F0F0F', text_color: '#FFFFFF',
  show_to_segment: 'all', show_frequency: 'once',
  is_enabled: true, is_current: false
};

export const AcresBannerAdmin: React.FC<{ currentUser?: any }> = () => {
  const [versions, setVersions] = useState<Banner[]>([]);
  const [editing, setEditing] = useState<Banner | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try { const r = await acresAdmin.listBannerVersions(); setVersions(r.versions || []); }
    catch (e: any) { setError(e?.message || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.version_label || !editing.title) { alert('Version label and title required'); return; }
    try { await acresAdmin.upsertBanner(editing); setEditing(null); await load(); }
    catch (e: any) { alert(e?.message || 'Save failed'); }
  };
  const setCurrent = async (b: Banner) => {
    if (!b.id) return;
    if (!confirm(`Make "${b.version_label}" the active banner shown to users?`)) return;
    try { await acresAdmin.setBannerCurrent(b.id); await load(); }
    catch (e: any) { alert(e?.message); }
  };
  const toggleEnabled = async (b: Banner) => {
    if (!b.id) return;
    try { await acresAdmin.setBannerEnabled(b.id, !b.is_enabled); await load(); }
    catch (e: any) { alert(e?.message); }
  };

  return (
    <div>
      {error && <div style={errBox}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontWeight: 600 }}>Banner Versions ({versions.length})</div>
        <button onClick={() => setEditing({ ...empty, version_label: 'v' + (versions.length + 1) })} style={btnPrimary}>+ New Version</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: editing ? '1fr 1fr' : '1fr', gap: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr style={trHead}><th style={th}>Version</th><th style={th}>Title</th><th style={th}>Segment</th><th style={th}>Freq</th><th style={th}>Status</th><th style={th}></th></tr></thead>
            <tbody>
              {versions.map(b => (
                <tr key={b.id} style={trBody}>
                  <td style={td}>{b.version_label}</td>
                  <td style={td}>{b.title}</td>
                  <td style={td}>{b.show_to_segment}</td>
                  <td style={td}>{b.show_frequency}</td>
                  <td style={td}>{b.is_current ? '⭐ live' : b.is_enabled ? '🟢' : '⚪'}</td>
                  <td style={td}>
                    <button onClick={() => setEditing(b)} style={btnSmall}>Edit</button>
                    {!b.is_current && <button onClick={() => setCurrent(b)} style={btnSmall}>Set Live</button>}
                    <button onClick={() => toggleEnabled(b)} style={btnSmall}>{b.is_enabled ? 'Disable' : 'Enable'}</button>
                  </td>
                </tr>
              ))}
              {!versions.length && <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>No banners yet</td></tr>}
            </tbody>
          </table>
        </div>

        {editing && (
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>{editing.id ? 'Edit Banner' : 'New Banner'}</div>
            <Field label="Version label*"><input style={input} value={editing.version_label} onChange={e => setEditing({ ...editing, version_label: e.target.value })} /></Field>
            <Field label="Title*"><input style={input} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Body (markdown)"><textarea style={{ ...input, minHeight: 80 }} value={editing.body_md} onChange={e => setEditing({ ...editing, body_md: e.target.value })} /></Field>
            <div style={grid}>
              <Field label="CTA label"><input style={input} value={editing.cta_label} onChange={e => setEditing({ ...editing, cta_label: e.target.value })} /></Field>
              <Field label="CTA URL"><input style={input} value={editing.cta_url} onChange={e => setEditing({ ...editing, cta_url: e.target.value })} /></Field>
              <Field label="Image URL"><input style={input} value={editing.image_url || ''} onChange={e => setEditing({ ...editing, image_url: e.target.value })} /></Field>
              <Field label="Background"><input style={input} type="color" value={editing.bg_color || '#0F0F0F'} onChange={e => setEditing({ ...editing, bg_color: e.target.value })} /></Field>
              <Field label="Text color"><input style={input} type="color" value={editing.text_color || '#FFFFFF'} onChange={e => setEditing({ ...editing, text_color: e.target.value })} /></Field>
              <Field label="Segment">
                <select style={input} value={editing.show_to_segment} onChange={e => setEditing({ ...editing, show_to_segment: e.target.value as any })}>
                  <option value="all">All users</option><option value="kyc_complete">KYC complete</option><option value="has_shares">Has shares</option><option value="no_shares">No shares yet</option>
                </select>
              </Field>
              <Field label="Frequency">
                <select style={input} value={editing.show_frequency} onChange={e => setEditing({ ...editing, show_frequency: e.target.value as any })}>
                  <option value="once">Once</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="every_login">Every login</option>
                </select>
              </Field>
              <Field label="Start at"><input type="datetime-local" style={input} value={editing.start_at?.slice(0,16) || ''} onChange={e => setEditing({ ...editing, start_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></Field>
              <Field label="End at"><input type="datetime-local" style={input} value={editing.end_at?.slice(0,16) || ''} onChange={e => setEditing({ ...editing, end_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></Field>
            </div>

            <div style={{ marginTop: 12, padding: 14, borderRadius: 10, background: editing.bg_color || '#0F0F0F', color: editing.text_color || '#FFF', border: '1px solid #2A2A2A' }}>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>LIVE PREVIEW</div>
              {editing.image_url && <img src={editing.image_url} alt="" style={{ maxHeight: 80, marginBottom: 6 }} />}
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{editing.title}</div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, marginBottom: 8 }}>{editing.body_md}</div>
              <button style={{ padding: '6px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700 }}>{editing.cta_label}</button>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setEditing(null)} style={btnGhost}>Cancel</button>
              <button onClick={save} style={btnPrimary}>Save</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 8 }}><div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>{label}</div>{children}</div>
);
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px' };
const card: React.CSSProperties = { background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8, padding: 14 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 13 };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 };
const btnPrimary: React.CSSProperties = { padding: '8px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', marginRight: 6, background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 4, fontSize: 12, cursor: 'pointer' };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
